import { Component, computed, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  base64Decode,
  base64Encode,
  explore,
  Filter,
  FilterService,
  Sort,
  SortService,
  Store,
  withFilter,
  withSearch,
  withSort } from '@softline/core';
import { LlrBestellungStore } from '../../store/bestellung.store';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  FilterAndSortComponent,
  FilterConfig,
  Scan,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SCANNER,
  SOFTLINE_FEATURE_TITLE,
  SortConfig,
  TitleStore } from '@softline/application';
import { ModalStore, SOFTLINE_FEATURE_MODAL} from '@softline/ui-core';
import { combineLatestWith, map, Observable, skip, Subscription } from 'rxjs';
import { Bestellung } from '../../types/bestellung';
import { ActivatedRoute, Router } from '@angular/router';
import { LlrKommissionierungStore } from '../../store/kommissionierung.store';
import { UserInfoService } from '@softline/auth';
import { BestellungUebernehmenDialogComponent } from '../../dialogs/bestellung-uebernehmen/bestellung-uebernehmen-dialog.component';
import { Deactivateable } from '../../guards/leave-bestellungen.guard';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG } from '../../kommissionierung.shared';

import { FormatPipe } from '@softline/ui-core';
import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-warenzugang',
  imports: [CommonModule, FilterAndSortComponent,
    FormatPipe,
    RepeatDirective,
    TranslatePipe],
  templateUrl: './bestellungen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./bestellungen.component.css'] })
export class BestellungenComponent
  implements OnInit, OnDestroy, BackNavigable, Deactivateable
{
  private scanSubscription?: Subscription;

  readonly bestellungen$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
    LlrBestellungStore.getters.all
  );

  private readonly userInfo = inject(UserInfoService);

  readonly currentAnwender = computed(() => ({
    username: this.userInfo.value()?.userName,
  }));

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
    LlrBestellungStore.getters.loading
  );

  readonly bestellungen = toSignal(this.bestellungen$);

  readonly explorer = explore(this.bestellungen, withFilter(), withSort(), withSearch());

  filterConfig: FilterConfig = {
    properties: [
      {
        property: 'id',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BESTELLUNG_ID' },
      {
        property: 'lieferant.nummer',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_LIEFERANT_NUMMER' },
      {
        property: 'lieferant.kurzbez',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_LIEFERANT_BEZEICHNUNG' },
      {
        property: 'belegnummer',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BELEGNUMMER' },
      {
        property: 'belegddatum',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BELEGDATUM' }] };

  sortConfig: SortConfig = {
    properties: [
      {
        property: 'id',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BESTELLUNG_ID' },
      {
        property: 'lieferant.nummer',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_LIEFERANT_NUMMER' },
      {
        property: 'lieferant.kurzbez',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_LIEFERANT_BEZEICHNUNG' },
      {
        property: 'belegnummer',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BELEGNUMMER' },
      {
        property: 'belegddatum',
        title: '#LLR_KOMMISSIONIERUNG.SORT_FILTER_CONFIG.LABEL_BELEGDATUM' }] };
  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sortService: SortService,
    private filterService: FilterService,
    private backNavigationService: BackNavigationService
  ) {}
  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setSubTitle,
      '#LLR_KOMMISSIONIERUNG.WARENZUGANG.SUBTITEL_BESTELLUNGEN'
    );

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: BestellungenComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_BESTELLUNG_SCANNEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-barcode-scan',
          execute: async () => this.scanOffeneBestellung(undefined) }] });

    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
        LlrBestellungStore.actions.loadMany
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }

    this.scanSubscription = this.store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(skip(1))
      .subscribe(async (scan) => {
        await this.scanOffeneBestellung(scan);
      });
  }

  async ngOnDestroy(): Promise<void> {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');

    this.store.commit(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
      LlrBestellungStore.mutations.clear
    );

    this.store.commit(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
      LlrBestellungStore.mutations.setFilter,
      null
    );

    this.store.commit(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
      LlrBestellungStore.mutations.setSort,
      null
    );

    if (this.scanSubscription && !this.scanSubscription.closed)
      this.scanSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
    this.scanSubscription = undefined;
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  async canDeactivate(params: any): Promise<boolean> {
    try {
      const userInfoValue = this.userInfo.value();

      const decoedParams = JSON.parse(base64Decode(params));

      if (decoedParams.anwender && decoedParams.anwender !== userInfoValue?.userName) {
        const dialogResult = await this.store.dispatch(
          SOFTLINE_FEATURE_MODAL,
          ModalStore.actions.open<boolean, object>(),
          {
            id: 'SOFTLINE_DIALOG_BESTELLUNG_UEBERNEHMEN',
            component: BestellungUebernehmenDialogComponent,
            dismiss: true }
        );

        if (!dialogResult || dialogResult === 'DISMISSED') return false;

        await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
          LlrKommissionierungStore.actions.kommissionierungUebernehmen,
          {
            idkopfbskomm: decoedParams.idkopf }
        );
      } else {
        await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
          LlrKommissionierungStore.actions.kommissionierungErstellen,
          {
            idkopfbs: decoedParams.id }
        );
      }
      return true;
    } catch (e) {
      showRequestErrors(this.store, e);

      try {
        await this.store.dispatch(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
          LlrBestellungStore.actions.loadMany
        );
        return false;
      } catch (e) {
        showRequestErrors(this.store, e);
        return false;
      }
    }
  }

  async scanOffeneBestellung(scan: Scan | undefined): Promise<void> {
    try {
      if (!scan) {
        scan = await this.store.dispatch(
          SOFTLINE_FEATURE_SCANNER,
          ScannerStore.actions.scan,
          {
            labelType: ['ean13', 'ean128', 'code39', 'code128', 'ean8'] }
        );
      }

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_BESTELLUNG,
        LlrBestellungStore.actions.scanBestellung,
        {
          code: scan.data,
          type: scan.labelType ?? '' }
      );

      if (!result || Object.keys(result).length === 0) return;

      await this.router.navigate(['/llr-bskomm/artikelauswahl'], {
        queryParams: {
          params: base64Encode(
            JSON.stringify({
              id: result?.id,
              idkopf: result?.kopfbskommExzessResult?.idkopf,
              anwender: result?.kopfbskommExzessResult?.anwender?.anwender })
          ) } });
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async artikelAuswahlLaden(bestellung: Bestellung): Promise<void> {
    try {
      await this.router.navigate(['/llr-bskomm/artikelauswahl'], {
        queryParams: {
          params: base64Encode(
            JSON.stringify({
              id: bestellung?.id,
              idkopf: bestellung?.kopfbskommExzessResult?.idkopf,
              anwender: bestellung?.kopfbskommExzessResult?.anwender?.anwender })
          ) } });
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
